import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Alert,
  Paper,
  Button
} from '@mui/material';
import { 
  Gavel,
  Security,
  AccountBalance,
  Warning,
  Info,
  CheckCircle,
  Star,
  Person,
  Link,
  Verified,
  Shield,
  Balance,
  Business,
  Cancel,
  Update
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();

  const handleBackToRegister = () => {
    navigate('/register');
  };

  const competitionRules = [
    {
      title: "Eligibility Requirements",
      icon: <Person color="primary" />,
      rules: [
        "Must be 18 years and older",
        "Verified trading account with XM Global or Weltrade",
        "Account must be in good standing",
        "All accounts must be affiliated with T1 FX Team",
        "KYC verification completed",
        "Registration required before competition starts",
        "Valid contact information provided"
      ]
    },
    {
      title: "Trading Requirements",
      icon: <AccountBalance color="primary" />,
      rules: [
        "All trades must be open for more than 3 minutes to qualify",
        "Percentage Growth participants must enter on XM Global",
        "Confirmation of affiliation with T1 FX Team required",
        "Account must remain active throughout competition period",
        "No scalping or hedging strategies allowed",
        "Minimum deposit requirements must be maintained"
      ]
    },
    {
      title: "Points System",
      icon: <Star color="primary" />,
      rules: [
        "Gold trading: 1 point per lot traded",
        "Bitcoin (BTC) trading: 2 points per lot traded", 
        "Silver trading: 3 points per lot traded",
        "Points calculated based on total lots traded × instrument multiplier",
        "All trades must be completed during competition period",
        "Points are calculated in real-time and updated daily"
      ]
    }
  ];

  const legalTerms = [
    {
      title: "Platform Usage",
      icon: <Security color="primary" />,
      terms: [
        "You must provide accurate and complete information during registration",
        "You are responsible for maintaining the confidentiality of your account credentials",
        "You must not share your account with other users",
        "You agree to use the platform only for lawful purposes",
        "You must not attempt to manipulate or exploit the competition system",
        "Fraudulent activity will result in immediate disqualification"
      ]
    },
    {
      title: "Prize Distribution",
      icon: <Gavel color="primary" />,
      terms: [
        "Prizes will be distributed only to verified participants",
        "Winners must provide valid tax identification information",
        "Prizes may be subject to applicable taxes and regulations",
        "FX Wars reserves the right to withhold prizes pending verification",
        "Prize distribution timeline may vary based on verification process",
        "Unclaimed prizes will be forfeited after 90 days"
      ]
    },
    {
      title: "Liability and Risk",
      icon: <Warning color="primary" />,
      terms: [
        "Trading involves substantial risk of loss and may not be suitable for all investors",
        "Past performance does not guarantee future results",
        "You acknowledge the risks associated with forex trading",
        "FX Wars is not responsible for trading losses incurred during competition",
        "Participants trade at their own risk and discretion",
        "Market volatility may affect competition results"
      ]
    },
    {
      title: "Data Protection & Privacy",
      icon: <Shield color="primary" />,
      terms: [
        "We collect and process personal data in accordance with our Privacy Policy",
        "Your data will be used for competition administration and communication",
        "We implement appropriate security measures to protect your information",
        "You have the right to access, correct, or delete your personal data",
        "Data may be shared with partner brokers for verification purposes",
        "Marketing communications can be opted out at any time"
      ]
    },
    {
      title: "Intellectual Property",
      icon: <Business color="primary" />,
      terms: [
        "All content on this platform is owned by FX Wars or its licensors",
        "You may not reproduce, distribute, or modify any platform content",
        "Trademarks and logos are protected intellectual property",
        "User-generated content may be used for promotional purposes",
        "You grant FX Wars a license to use your competition results for marketing",
        "Third-party trademarks are property of their respective owners"
      ]
    },
    {
      title: "Platform Modifications",
      icon: <Update color="primary" />,
      terms: [
        "FX Wars reserves the right to modify these terms at any time",
        "Changes will be communicated via email and platform notifications",
        "Continued use of the platform constitutes acceptance of modified terms",
        "Competition rules may be updated before or during competitions",
        "Platform features and functionality may be changed without notice",
        "Service availability is not guaranteed and may be interrupted"
      ]
    }
  ];

  return (
    <Box sx={{ pt: 12, pb: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              Terms & Conditions
            </Typography>
            <Typography variant="h5" color="text.secondary" mb={2}>
              FX Wars Trading Competition Platform
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
              Please read these terms and conditions carefully before participating in FX Wars competitions. 
              By registering and using our platform, you agree to comply with all terms outlined below.
            </Typography>
          </Box>
        </motion.div>

        {/* Competition Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              🗓️ Official Competition Timeline
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Competition Start
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    June 1, 2025
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    00:00:00 UTC+2
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Competition End
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#FF5722', fontWeight: 'bold' }}>
                    December 31, 2025
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    23:59:59 UTC+2
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Duration
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#FFC107', fontWeight: 'bold' }}>
                    7 Months
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    214 Trading Days
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Last Updated */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Last Updated:</strong> January 2025 | 
            <strong> Effective Date:</strong> January 1, 2025 | 
            <strong> Version:</strong> 1.0
          </Typography>
        </Alert>

        {/* Quick Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Paper sx={{ p: 3, mb: 6, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              📋 Quick Reference
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Person sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Minimum Age</Typography>
                  <Typography variant="body2">18+ years old</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Required Platforms</Typography>
                  <Typography variant="body2">XM Global & Weltrade</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Link sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Affiliation</Typography>
                  <Typography variant="body2">T1 FX Team Required</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Verified sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Verification</Typography>
                  <Typography variant="body2">KYC & Identity</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Competition Rules Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Star color="primary" sx={{ fontSize: 40 }} />
              Competition Rules & Requirements
            </Typography>
            <Grid container spacing={4}>
              {competitionRules.map((section, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' } }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        {section.icon}
                        <Typography variant="h6" fontWeight="bold" ml={1}>
                          {section.title}
                        </Typography>
                      </Box>
                      <List dense>
                        {section.rules.map((rule, ruleIndex) => (
                          <ListItem key={ruleIndex} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />
                            </ListItemIcon>
                            <ListItemText primary={rule} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        <Divider sx={{ my: 6 }} />

        {/* Legal Terms Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Balance color="primary" sx={{ fontSize: 40 }} />
              Legal Terms & Conditions
            </Typography>
            <Grid container spacing={4}>
              {legalTerms.map((section, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' } }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        {section.icon}
                        <Typography variant="h6" fontWeight="bold" ml={1}>
                          {section.title}
                        </Typography>
                      </Box>
                      <List dense>
                        {section.terms.map((term, termIndex) => (
                          <ListItem key={termIndex} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <CheckCircle sx={{ fontSize: 16, color: '#2196F3' }} />
                            </ListItemIcon>
                            <ListItemText primary={term} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Trading Risk Disclaimer */}
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            ⚠️ Trading Risk Disclaimer
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            Trading in financial markets carries a high level of risk and may not be suitable for all investors. 
            Before deciding to trade, you should carefully consider your investment objectives, level of experience, 
            and risk appetite. The possibility exists that you could sustain a loss of some or all of your initial 
            investment and therefore you should not invest money that you cannot afford to lose. You should be aware 
            of all the risks associated with trading and seek advice from an independent financial advisor if you 
            have any doubts. Past performance is not indicative of future results.
          </Typography>
        </Alert>

        {/* Termination and Violations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Card sx={{ mb: 6, border: '2px solid #f44336' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Cancel color="error" sx={{ fontSize: 30, mr: 2 }} />
                <Typography variant="h5" fontWeight="bold" color="error">
                  Account Termination & Violations
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                FX Wars reserves the right to terminate or suspend your account immediately, without prior notice, 
                for any of the following violations:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Cancel color="error" sx={{ fontSize: 20 }} /></ListItemIcon>
                  <ListItemText primary="Providing false or misleading information during registration" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Cancel color="error" sx={{ fontSize: 20 }} /></ListItemIcon>
                  <ListItemText primary="Attempting to manipulate competition results or rankings" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Cancel color="error" sx={{ fontSize: 20 }} /></ListItemIcon>
                  <ListItemText primary="Using multiple accounts or sharing account credentials" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Cancel color="error" sx={{ fontSize: 20 }} /></ListItemIcon>
                  <ListItemText primary="Engaging in fraudulent trading activities or money laundering" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Cancel color="error" sx={{ fontSize: 20 }} /></ListItemIcon>
                  <ListItemText primary="Violating any applicable laws, regulations, or platform policies" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact and Dispute Resolution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Card sx={{ mb: 6, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center" color="primary">
                📞 Contact & Dispute Resolution
              </Typography>
              <Typography variant="body1" paragraph textAlign="center">
                For questions about these terms or to report violations, please contact our support team:
              </Typography>
              <Box textAlign="center">
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/contact')}
                  sx={{ mr: 2, mb: 1 }}
                >
                  Contact Support
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/rules')}
                  sx={{ mb: 1 }}
                >
                  View Competition Rules
                </Button>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                All disputes will be handled in accordance with applicable laws and regulations. 
                We aim to resolve all issues fairly and transparently.
              </Typography>
            </CardContent>
          </Card>
        </motion.div>

        {/* Back to Registration */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Button 
            variant="contained" 
            size="large"
            onClick={handleBackToRegister}
            sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem'
            }}
          >
            Back to Registration
          </Button>
        </Box>

        {/* Final Acknowledgment */}
        <Alert severity="success">
          <Typography variant="body1">
            <strong>Acknowledgment:</strong> By proceeding with registration, you acknowledge that you have read, 
            understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy. 
            If you do not agree with any part of these terms, please do not use our platform.
          </Typography>
        </Alert>
      </Container>
    </Box>
  );
};

export default TermsAndConditionsPage; 